import { inject, Injectable } from '@angular/core';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';

/**
 * Reads per-event access grants (`events/{eventId}/grants/{uid}`) — the
 * auth-uid-keyed role docs the submission/award CFs enforce. Shared seam so
 * components don't inject Firestore just to check access. Part of the WebAdmin
 * MVVM migration; see tools/no-firestore-in-components.mjs.
 */
@Injectable({ providedIn: 'root' })
export class GrantsRepository {
  private firestore = inject(Firestore);

  /** The user's whole grant for the event (role + report permissions), or null
   *  when there is none. A failed read is logged and answered with null, which
   *  opens nothing — the rules would refuse the reports' data anyway. */
  async getGrant(eventId: string, uid: string): Promise<Record<string, unknown> | null> {
    try {
      const snap = await getDoc(doc(this.firestore, `events/${eventId}/grants/${uid}`));
      return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
    } catch (err) {
      console.error(`grants: could not read the grant for ${eventId}`, err);
      return null;
    }
  }

  /** The signed-in user's role for the event, or undefined if no grant exists. */
  async getGrantRole(eventId: string, uid: string): Promise<string | undefined> {
    try {
      const snap = await getDoc(doc(this.firestore, `events/${eventId}/grants/${uid}`));
      return snap.exists() ? (snap.data() as { role?: string })?.role : undefined;
    } catch {
      return undefined;
    }
  }
}
